/** Whether a fundraising form may take submissions right now. */
export type FundraisingGateMode = 'live' | 'maintenance'

export interface FundraisingGateResult {
  mode: FundraisingGateMode
  /** True when no probe ran, so a host without one reads as healthy. */
  crmHealthy: boolean
  /** Whether staff have switched the forms off. False when the switch could not be read. */
  fallbackForms: boolean
}

export interface FundraisingGateOptions {
  /** The CMS `fallbackForms` switch: true takes the forms down. */
  readFallbackForms: () => Promise<boolean>
  /** The host's CRM health probe. Omit where the host has none. */
  checkCrmHealth?: () => Promise<boolean>
  /**
   * Where a swallowed `readFallbackForms` failure is reported. Required, and a host with nothing
   * to report passes an explicit no-op: the gate only gets to swallow that failure because someone
   * else is recording it.
   */
  onError: (error: unknown) => void | Promise<void>
}

/**
 * The gate both hosts run before a fundraising form renders: the CRM has to answer, and staff must
 * not have switched the forms off.
 *
 * A `readFallbackForms` that throws leaves the form up — a switch we cannot read must not take
 * donations down with it. A `checkCrmHealth` that throws propagates instead, because a probe that
 * could not run is no evidence the CRM can take a submission.
 */
export async function fundraisingGate({
  readFallbackForms,
  checkCrmHealth,
  onError,
}: FundraisingGateOptions): Promise<FundraisingGateResult> {
  const crmHealthy = checkCrmHealth ? await checkCrmHealth() : true

  let fallbackForms = false
  try {
    fallbackForms = await readFallbackForms()
  } catch (error) {
    // A reporter that throws must not turn a swallowed read into a failed gate: the point of
    // swallowing was to keep the form up, and losing the report is the lesser failure.
    try {
      await onError(error)
    } catch {
      // Nowhere left to report to.
    }
  }

  return { mode: crmHealthy && !fallbackForms ? 'live' : 'maintenance', crmHealthy, fallbackForms }
}
